/**
 * Инструкция для iPhone: iOS не показывает системное окно установки.
 *
 * Объясняем путь «Поделиться → На экран „Домой“» и показываем мок нижней
 * панели Safari с подсвеченной кнопкой «Поделиться» — по ней проще найти
 * нужный значок. Портал в body: модалка поверх шапки и нижнего меню.
 */

import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { IconX } from '../icons';

const STEPS = ['stepShare', 'stepAdd', 'stepConfirm', 'stepOpen'] as const;

/** Мок нижней панели Safari с подсвеченной кнопкой «Поделиться». */
function ShareToolbar() {
  const { t } = useTranslation();
  return (
    <figure className="flex flex-col items-center gap-2 rounded-card border border-line bg-surface-muted p-4">
      <svg viewBox="0 0 260 96" className="h-auto w-full max-w-[260px]" role="img" aria-label={t('install.shareHint')}>
        <rect x="8" y="58" width="244" height="34" rx="17" className="fill-surface stroke-line" strokeWidth="1.5" />
        <g className="stroke-muted" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M36 68l-5 6 5 6" />
          <path d="M58 68l5 6-5 6" />
          <path d="M208 68h14v14h-14z" />
          <path d="M224 73h6v9h-6z" />
        </g>
        <circle cx="140" cy="75" r="23" className="fill-accent/20 stroke-accent" strokeWidth="2" />
        <g transform="translate(126 61)" className="stroke-accent" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none">
          <path d="M8 11v7a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-7" />
          <path d="M14 17V4" />
          <path d="M10 8l4-4 4 4" />
        </g>
        <path d="M140 26v18" className="stroke-accent" strokeWidth="2" strokeDasharray="3 3" />
        <path d="M134 36l6 6 6-6" className="stroke-accent" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <figcaption className="text-caption text-muted">{t('install.shareHint')}</figcaption>
    </figure>
  );
}

export function IosInstallModal({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] grid place-items-center overflow-y-auto bg-heading/30 p-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={t('install.modalTitle')}
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative my-auto flex w-full max-w-md flex-col gap-4 rounded-card border border-line bg-[#FFFCF6] p-6 shadow-card-lg dark:bg-[#2B231D]"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t('install.bannerDismiss')}
          className="tap-target absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-full text-muted transition hover:bg-heading/10 hover:text-heading"
        >
          <IconX size={18} aria-hidden="true" />
        </button>
        <div className="flex flex-col gap-1 pr-10">
          <h2 className="t-h2">{t('install.modalTitle')}</h2>
          <p className="text-sm text-muted">{t('install.modalIntro')}</p>
        </div>
        <ShareToolbar />
        <ol className="flex flex-col gap-3">
          {STEPS.map((step, index) => (
            <li key={step} className="flex items-start gap-3">
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-accent font-heading text-xs font-extrabold text-on-accent">{index + 1}</span>
              <span className="text-sm text-heading">{t(`install.${step}`)}</span>
            </li>
          ))}
        </ol>
        <Button onClick={onClose}>{t('install.close')}</Button>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
